import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Save, FileText, Trash2, Edit, Plus, RefreshCw, ArrowLeft, Eye } from "lucide-react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../api/axios";

import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

import {
  masterFormCard,
  masterFormFieldGroup,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const isNoCategoryValue = (value) => {
  const v = String(value || "").trim().toUpperCase();
  return !v || v === "NO_CATEGORY" || v === "NO CATEGORY" || v === "NONE";
};

const getSubTopicName = (subTopic) =>
  typeof subTopic === "string" ? subTopic : String(subTopic?.subTopicName || subTopic?.name || "");

const getNestedTopicName = (nestedTopic) => String(nestedTopic?.nestedTopicName || nestedTopic?.name || "");

const StudyMaterialMaster = () => {
  const location = useLocation();
  const { id } = useParams();
  const fileInputRef = useRef(null);

  const [viewMode, setViewMode] = useState("form");
  const [editId, setEditId] = useState(id || null);
  const [materials, setMaterials] = useState([]);
  
  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    examMasterId: "",
    examCategoryId: "",
    subjectId: "",
    topicName: "",
    subTopicName: "",
    nestedTopicName: "",
    nestedSubTopicName: "",
    title: "",
    pdf: null,
    restrictionMode: true,
    status: "ACTIVE",
  });

  const [currentPdfName, setCurrentPdfName] = useState("");

  const fetchMasters = async () => {
    try {
      const [examRes, catRes, subRes] = await Promise.allSettled([
        axios.get("/master/exam"),
        axios.get("/master/category/all"),
        axios.get("/master/syllabus/all"),
      ]);
      setExams(examRes.status === "fulfilled" ? (Array.isArray(examRes.value.data) ? examRes.value.data : examRes.value.data?.data || []) : []);
      setCategories(catRes.status === "fulfilled" ? (Array.isArray(catRes.value.data) ? catRes.value.data : catRes.value.data?.data || []) : []);
      setSubjects(subRes.status === "fulfilled" ? (Array.isArray(subRes.value.data) ? subRes.value.data : subRes.value.data?.data || []) : []);
    } catch {
      toast.error("FAILED TO LOAD MASTERS");
    }
  };

  const modalFilteredCategories = useMemo(() => {
    if (!formData.examMasterId) return [];
    return categories.filter((cat) => String(cat.examCode || "").toUpperCase() === String(formData.examMasterId || "").toUpperCase());
  }, [categories, formData.examMasterId]);

  const modalNeedsCategory = useMemo(() => modalFilteredCategories.length > 0, [modalFilteredCategories]);

  const modalFilteredSubjects = useMemo(() => {
    if (!formData.examMasterId) return [];
    const needsCat = categories.some((cat) => String(cat.examCode || "").toUpperCase() === String(formData.examMasterId || "").toUpperCase());
    if (needsCat && !formData.examCategoryId) return [];
    return subjects.filter(
      (s) =>
        String(s.examCode || "").toUpperCase() === String(formData.examMasterId || "").toUpperCase() &&
        (!needsCat
          ? isNoCategoryValue(s.catId)
          : String(s.catId || "").toUpperCase() === String(formData.examCategoryId || "").toUpperCase())
    );
  }, [categories, subjects, formData.examMasterId, formData.examCategoryId]);

  const modalTopics = useMemo(() => {
    if (!formData.subjectId) return [];
    const selectedSub = subjects.find((s) => String(s.syllabusId || "").toUpperCase() === String(formData.subjectId || "").toUpperCase());
    return Array.isArray(selectedSub?.topics) ? selectedSub.topics : [];
  }, [subjects, formData.subjectId]);

  const modalSubTopics = useMemo(() => {
    if (!formData.topicName) return [];
    const selectedTop = modalTopics.find((t) => String(t.topicName || "").toUpperCase() === String(formData.topicName || "").toUpperCase());
    return Array.isArray(selectedTop?.subTopics) ? selectedTop.subTopics : [];
  }, [modalTopics, formData.topicName]);

  const modalNestedTopics = useMemo(() => {
    if (!formData.subTopicName) return [];
    const selectedSub = modalSubTopics.find((sub) => String(getSubTopicName(sub)).toUpperCase() === String(formData.subTopicName || "").toUpperCase());
    return Array.isArray(selectedSub?.nestedTopics || selectedSub?.childTopics) ? (selectedSub.nestedTopics || selectedSub.childTopics) : [];
  }, [modalSubTopics, formData.subTopicName]);

  const modalNestedSubTopics = useMemo(() => {
    if (!formData.nestedTopicName) return [];
    const selectedNested = modalNestedTopics.find((nt) => String(getNestedTopicName(nt)).toUpperCase() === String(formData.nestedTopicName || "").toUpperCase());
    return Array.isArray(selectedNested?.nestedSubTopics || selectedNested?.childTopics) ? (selectedNested.nestedSubTopics || selectedNested.childTopics) : [];
  }, [modalNestedTopics, formData.nestedTopicName]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/master/study-material/all");
      const list = res.data?.data || [];
      setMaterials(list);

      if (id) {
        const row = list.find((r) => String(r._id) === String(id));
        if (row) {
          setFormData({
            examMasterId: row.examMasterId || "",
            examCategoryId: row.examCategoryId === "NO_CATEGORY" ? "" : row.examCategoryId || "",
            subjectId: row.subjectId || "",
            topicName: row.topicName || "",
            subTopicName: row.subTopicName || "",
            nestedTopicName: row.nestedTopicName || "",
            nestedSubTopicName: row.nestedSubTopicName || "",
            title: row.title || "",
            pdf: null,
            status: row.status || "ACTIVE",
            restrictionMode: row.restrictionMode ?? true,
          });
          setCurrentPdfName(row.documentNames?.[0] || "");
          setEditId(row._id);
          setViewMode("form");
        }
      }
    } catch {
      toast.error("FAILED TO LOAD DATABASE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasters();
    fetchMaterials();
  }, [id]);

  const resetForm = () => {
    setEditId(null);
    setCurrentPdfName("");
    setFormData({
      examMasterId: "",
      examCategoryId: "",
      subjectId: "",
      topicName: "",
      subTopicName: "",
      nestedTopicName: "",
      nestedSubTopicName: "",
      title: "",
      pdf: null,
      restrictionMode: true,
      status: "ACTIVE",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.examMasterId) return toast.error("EXAM IS REQUIRED");
    if (!formData.subjectId) return toast.error("SUBJECT IS REQUIRED");
    if (!formData.title.trim()) return toast.error("TITLE IS REQUIRED");
    if (!editId && !formData.pdf) return toast.error("PDF FILE IS REQUIRED");

    setIsSaving(true);
    try {
      let base64Pdf = null;
      let fileName = null;

      if (formData.pdf) {
        base64Pdf = await convertFileToBase64(formData.pdf);
        fileName = formData.pdf.name;
      }

      const payload = {
        id: editId || undefined,
        examMasterId: formData.examMasterId,
        examCategoryId: formData.examCategoryId || "NO_CATEGORY",
        subjectId: formData.subjectId,
        topicName: formData.topicName.toUpperCase(),
        subTopicName: formData.subTopicName.toUpperCase(),
        nestedTopicName: formData.nestedTopicName.toUpperCase(),
        nestedSubTopicName: formData.nestedSubTopicName.toUpperCase(),
        title: formData.title.toUpperCase(),
        status: (formData.status || "ACTIVE").toUpperCase(),
        restrictionMode: formData.restrictionMode,
      };

      if (base64Pdf) {
        payload.documentPaths = [base64Pdf];
        payload.documentNames = [fileName];
      }

      const res = await axios.post("/master/study-material/upsert", payload);

      if (res.data.success || res.status >= 200) {
        toast.success(editId ? "RECORD UPDATED" : "RECORD SAVED");
        resetForm();
        fetchMaterials();
        setViewMode("form");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "SAVE FAILED - CHECK SERVER");
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewPdf = async (recordId) => {
    try {
      const res = await axios.get(`/master/study-material/${recordId}`);
      const docItem = res.data?.data;
      if (docItem && docItem.documentPaths && docItem.documentPaths.length > 0) {
        const base64Str = docItem.documentPaths[0];
        const win = window.open();
        const byteCharacters = atob(base64Str);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        win.location.href = blobUrl;
      } else {
        toast.error("PDF NOT FOUND");
      }
    } catch {
      toast.error("FAILED TO OPEN PDF");
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setCurrentPdfName(item.documentNames?.[0] || "");
    setFormData({
      examMasterId: item.examMasterId || "",
      examCategoryId: item.examCategoryId === "NO_CATEGORY" ? "" : item.examCategoryId || "",
      subjectId: item.subjectId || "",
      topicName: item.topicName || "",
      subTopicName: item.subTopicName || "",
      nestedTopicName: item.nestedTopicName || "",
      nestedSubTopicName: item.nestedSubTopicName || "",
      title: item.title || "",
      pdf: null,
      status: item.status || "ACTIVE",
      restrictionMode: item.restrictionMode ?? true,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setViewMode("form");
  };

  const handleDelete = async (recordId) => {
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) {
      try {
        await axios.delete(`/master/study-material/${recordId}`);
        toast.success("DELETED SUCCESSFULLY");
        fetchMaterials();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  const filteredData = materials.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(term) ||
      item.examName?.toLowerCase().includes(term) ||
      item.categoryName?.toLowerCase().includes(term) ||
      item.subjectName?.toLowerCase().includes(term) ||
      item.topicName?.toLowerCase().includes(term) ||
      item.materialId?.toLowerCase().includes(term)
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-1`}>
      {viewMode === "form" ? (
        <div>
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <FileText size={13} />
                </div>
                <h1 className={masterHeadingMain}>{editId ? "Edit Study Material" : "Study Material Form"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("list");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`${masterFormCard} p-2 flex flex-col gap-3 mt-1`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Exam</label>
                <select
                  value={formData.examMasterId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      examMasterId: e.target.value,
                      examCategoryId: "",
                      subjectId: "",
                      topicName: "",
                      subTopicName: "",
                      nestedTopicName: "",
                      nestedSubTopicName: "",
                    })
                  }
                  className={masterSelect}
                  required
                >
                  <option value="">SELECT EXAM</option>
                  {exams.map((ex) => (
                    <option key={ex.examCode} value={ex.examCode}>
                      {ex.examName}
                    </option>
                  ))}
                </select>
              </div>

              {modalNeedsCategory && (
                <div className={masterFormFieldGroup}>
                  <label className={masterInputLabel}>Category</label>
                  <select
                    value={formData.examCategoryId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        examCategoryId: e.target.value,
                        subjectId: "",
                        topicName: "",
                        subTopicName: "",
                        nestedTopicName: "",
                        nestedSubTopicName: "",
                      })
                    }
                    className={masterSelect}
                    required
                  >
                    <option value="">SELECT CATEGORY</option>
                    {modalFilteredCategories.map((cat) => (
                      <option key={cat.catId} value={cat.catId}>
                        {cat.catName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Subject</label>
                <select
                  value={formData.subjectId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subjectId: e.target.value,
                      topicName: "",
                      subTopicName: "",
                      nestedTopicName: "",
                      nestedSubTopicName: "",
                    })
                  }
                  className={masterSelect}
                  required
                >
                  <option value="">SELECT SUBJECT</option>
                  {modalFilteredSubjects.map((sub) => (
                    <option key={sub.syllabusId} value={sub.syllabusId}>
                      {sub.subjectName}
                    </option>
                  ))}
                </select>
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Topic</label>
                <select
                  value={formData.topicName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      topicName: e.target.value,
                      subTopicName: "",
                      nestedTopicName: "",
                      nestedSubTopicName: "",
                    })
                  }
                  className={masterSelect}
                >
                  <option value="">SELECT TOPIC (OPTIONAL)</option>
                  {modalTopics.map((top, idx) => (
                    <option key={idx} value={top.topicName}>
                      {top.topicName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Sub-Topic</label>
                <select
                  value={formData.subTopicName}
                  onChange={(e) => setFormData({ ...formData, subTopicName: e.target.value, nestedTopicName: "", nestedSubTopicName: "" })}
                  className={masterSelect}
                >
                  <option value="">SELECT SUB-TOPIC (OPTIONAL)</option>
                  {modalSubTopics.map((subTop, idx) => (
                    <option key={idx} value={getSubTopicName(subTop)}>
                      {getSubTopicName(subTop)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Nested Topic</label>
                <select
                  value={formData.nestedTopicName}
                  onChange={(e) => setFormData({ ...formData, nestedTopicName: e.target.value, nestedSubTopicName: "" })}
                  className={masterSelect}
                >
                  <option value="">SELECT NESTED TOPIC (OPTIONAL)</option>
                  {modalNestedTopics.map((nt, idx) => (
                    <option key={idx} value={getNestedTopicName(nt)}>
                      {getNestedTopicName(nt)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Nested Sub-Topic</label>
                <select
                  value={formData.nestedSubTopicName}
                  onChange={(e) => setFormData({ ...formData, nestedSubTopicName: e.target.value })}
                  className={masterSelect}
                >
                  <option value="">SELECT NESTED SUB-TOPIC (OPTIONAL)</option>
                  {modalNestedSubTopics.map((sst, idx) => (
                    <option key={idx} value={String(sst || "")}>
                      {String(sst || "")}
                    </option>
                  ))}
                </select>
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value.toUpperCase() })
                  }
                  placeholder="ENTER TITLE"
                  className={masterInput}
                  required
                />
              </div>
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Restriction Mode</label>
                <div className="flex items-center h-[32px] gap-2 px-1">
                  <input
                    type="checkbox"
                    checked={formData.restrictionMode}
                    onChange={(e) => setFormData({ ...formData, restrictionMode: e.target.checked })}
                    className="w-4 h-4 text-[#0E3652] rounded border-slate-300 focus:ring-[#0E3652]"
                  />
                  <span className="text-xs font-semibold text-slate-700">Enable Anti-Cheating Restrictions</span>
                </div>
              </div>
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            <div className="w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>
                  Upload PDF {currentPdfName && <span className="text-blue-600 font-normal">({currentPdfName})</span>}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    setFormData({ ...formData, pdf: e.target.files[0] });
                    if (e.target.files[0]) setCurrentPdfName(e.target.files[0].name);
                  }}
                  className={masterInput}
                  required={!editId}
                />
              </div>
            </div>

            <div className="flex items-center justify-between w-full pt-1">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>

              <div>
                <button disabled={isSaving || loading} type="submit" className={masterFormBtnSubmit}>
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editId ? "UPDATE" : "SAVE"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          <div className={`${masterPanelCard} p-1 mb-1`}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <FileText size={13} />
                </div>
                <h1 className={masterHeadingMain}>Study Material List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchMaterials}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH MATERIAL ID, EXAM, TOPIC, OR TITLE..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-1`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM NAME</th>
                    <th className={masterThName}>SUBJECT</th>
                    <th className={masterThName}>TOPIC</th>
                    <th className={masterThName}>SUB-TOPIC</th>
                    <th className={masterThName}>NESTED TOPIC</th>
                    <th className={masterThName}>NESTED SUB-TOPIC</th>
                    <th className={masterThName}>TITLE</th>
                    <th className={masterThName}>PDF NAME</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="11" className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{item.examName}</td>
                        <td className={masterTdName}>{item.subjectName}</td>
                        <td className={masterTdName}>{item.topicName}</td>
                        <td className={masterTdName}>{item.subTopicName}</td>
                        <td className={masterTdName}>{item.nestedTopicName || "---"}</td>
                        <td className={masterTdName}>{item.nestedSubTopicName || "---"}</td>
                        <td className={masterTdName}>{item.title}</td>
                        <td className={masterTdName}>{item.documentNames?.[0] || "---"}</td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              item.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleViewPdf(item._id)}
                              className={masterActionBtnEdit}
                              title="View PDF"
                            >
                              <Eye size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyMaterialMaster;
